export const en = {
  // Navigation
  nav: {
    home: "Home",
    product: "Solutions",
    howItWorks: "How It Works",
    pricing: "Pricing",
    services: "Services",
    benchmarks: "Benchmarks",
    about: "About",
    contact: "Contact",
    blog: "Blog",
    help: "Help",
    status: "Status",
    content: "Content",
    more: "More",
  },

  // Common
  common: {
    learnMore: "Learn more",
    getStarted: "Get started",
    contactUs: "Contact us",
    talkOnWhatsApp: "Talk on WhatsApp",
    sendMessage: "Send message",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    required: "Required",
    optional: "Optional",
    yes: "Yes",
    no: "No",
    notSure: "Not sure",
    other: "Other",
    readMore: "Read more",
    backToBlog: "Back to blog",
    publishedOn: "Published on",
  },

  // Footer
  footer: {
    tagline: "Automation that moves revenue.",
    rights: "All rights reserved.",
    terms: "Terms of Use",
    privacy: "Privacy Policy",
    commercial: "Commercial Terms",
    product: "Solutions",
    company: "Company",
    legal: "Legal",
    platforms: "Platforms",
  },

  // Home Page
  home: {
    heroTitle: "We help your operation start and grow.",
    heroTitleHighlight: "",
    heroSubtitle: "Store setup, automations, AI, integrations, and dashboards for businesses building or organizing their online operation.",
    heroCta: "Talk to ProcessSurge",
    heroSecondary: "See how it works",
    
    // Features section
    featuresTitle: "Why ProcessSurge?",
    featuresSubtitle: "Structure, automation, and clarity for businesses selling online and looking to grow consistently.",
    
    feature1Title: "A technical partner",
    feature1Desc: "We support the start of the operation and keep helping as it grows.",
    
    feature2Title: "Connected solutions",
    feature2Desc: "Store, revenue recovery, AI, integrations, and data working together.",
    
    feature3Title: "No unnecessary complexity",
    feature3Desc: "We recommend and integrate the right tools for your stage without forcing unnecessary changes.",
    
    feature4Title: "Clear delivery",
    feature4Desc: "Scope, operation, and results explained in a simple and direct way.",
    
    // Services preview
    servicesTitle: "From store launch to a more mature operation",
    servicesSubtitle: "We help you organize the present and prepare the next step.",
    
    // CTA section
    ctaTitle: "Want to understand what makes sense for your stage?",
    ctaSubtitle: "The diagnosis is simple, consultative, and commitment-free.",
    ctaCta: "Talk to the team",
  },

  // Product Page
  product: {
    heroTitle: "ProcessSurge Cart Recovery",
    heroSubtitle: "Abandoned cart recovery for VTEX and Shopify with performance-based pricing.",
    
    forWhoTitle: "Who it's for",
    forWhoDesc: "E-commerce brands and operations on VTEX or Shopify that want to recover lost sales without risking initial investment.",
    
    whatItDoesTitle: "What we deliver",
    whatItDoes1: "Real-time identification of abandoned carts",
    whatItDoes2: "Message automation via email, SMS, or WhatsApp",
    whatItDoes3: "Automatic stop rules when purchase is completed",
    whatItDoes4: "Performance and attribution reports",
    
    antiSpamTitle: "How we avoid spam",
    antiSpamDesc: "Your brand is priority. We implement:",
    antiSpam1: "Frequency limit per contact",
    antiSpam2: "Responsible time windows",
    antiSpam3: "Immediate stop rules on purchase or unsubscribe",
    antiSpam4: "Respect for customer channel preferences",
    
    requirementsTitle: "What you need to provide",
    requirements1: "Platform access (VTEX or Shopify)",
    requirements2: "Configured messaging channels (email, SMS, or WhatsApp Business)",
    requirements3: "Privacy policy and consent alignment",
    
    ctaTitle: "Want to understand how it works in practice?",
    ctaCta: "Request demo",
  },

  // How It Works
  howItWorks: {
    heroTitle: "How it works",
    heroSubtitle: "From diagnosis to recovery in 4 simple steps.",
    
    step1Title: "Diagnosis",
    step1Desc: "We analyze your operation, abandonment volume, and available channels to define the best strategy.",
    
    step2Title: "Integration",
    step2Desc: "We connect with your platform (VTEX or Shopify) and configure the automation flows.",
    
    step3Title: "Activation",
    step3Desc: "Automations go live with continuous monitoring and frequency adjustments.",
    
    step4Title: "Results",
    step4Desc: "You track results in real-time and pay only for effectively recovered carts.",
    
    timelineNote: "Implementation time depends on operation complexity and access availability.",
  },

  // Pricing
  pricing: {
    heroTitle: "Transparent pricing",
    heroSubtitle: "Performance model: you only pay when there are results.",
    
    modelTitle: "How billing works",
    modelDesc: "We charge a success fee only on orders recovered through our automations.",
    
    whatIsRecoveredTitle: "What counts as 'recovered'",
    whatIsRecoveredDesc: "A cart is considered recovered when:",
    recovered1: "The customer received our communication",
    recovered2: "Completed the purchase within the agreed attribution window",
    recovered3: "The order was not canceled or refunded",
    
    whatIsNotChargedTitle: "What we DON'T charge",
    notCharged1: "Initial setup or implementation",
    notCharged2: "Fixed monthly fees",
    notCharged3: "Purchases that would have happened anyway",
    notCharged4: "Orders outside the attribution window",
    
    messagingCostsTitle: "Messaging costs",
    messagingCostsDesc: "Message sending costs (SMS, WhatsApp, transactional email) are the client's responsibility or passed through as agreed in contract.",
    
    auditTitle: "Audit and transparency",
    auditDesc: "We provide detailed reports for verification. The definition of 'recovered' is agreed before start and documented in contract.",
    
    ctaTitle: "Want a personalized proposal?",
    ctaCta: "Request proposal",
  },

  // Services
  services: {
    heroTitle: "Automation Services",
    heroSubtitle: "ProcessSurge is a tech company specialized in custom automations and integrations.",
    
    integrationsTitle: "System Integrations",
    integrationsDesc: "We connect your tools and platforms through APIs and connectors. Eliminate manual data transfer work between systems.",
    integrationsFor: "For: operations using multiple tools that don't communicate with each other.",
    
    automationTitle: "Process Automation",
    automationDesc: "We automate repetitive tasks that consume your team's time. From approvals to periodic reports.",
    automationFor: "For: teams overloaded with repetitive operational work.",
    
    rpaTitle: "RPA (Robotic Process Automation)",
    rpaDesc: "Software robots that execute repetitive tasks across various systems, even without available APIs. Ideal for legacy systems.",
    rpaFor: "For: processes involving old systems or without direct integration possibility.",
    
    dataTitle: "Data Extraction and Organization",
    dataDesc: "We transform scattered data from different sources into usable routines and reports for decision making.",
    dataFor: "For: operations with data in spreadsheets, emails, various systems without consolidation.",
    
    aiTitle: "AI Agents",
    aiDesc: "Intelligent assistants for triage, initial support, and safer automations with human supervision.",
    aiFor: "For: operations that need to scale support or triage without losing control.",
    aiNote: "We implement with focus on security, human review, and governance. AI without control is risk, not solution.",
    
    ctaTitle: "Need a custom automation?",
    ctaCta: "Request diagnosis",
  },

  //calculadora surge assist
  supportSimulator: {
    title: "Support Savings Simulator",
    subtitle: "Compare the real cost per ticket between humans and AI.",
    ticketsLabel: "Tickets / month (Total)",
    agentsLabel: "Human Agents",
    hourlyLabel: "Hourly Cost (Salary + Taxes)",
    hourlyPlaceholder: "e.g.: 12",
    hoursLabel: "Monthly hours per agent",
    resolvedRateLabel: "AI Resolution Rate (%)",
    feePerResolvedLabel: "Surge AI fee per resolution",
    calculate: "Calculate Savings",
    resultTitle: "Estimated Financial Impact",
    ticketCostComparison: "Cost per Ticket (Unit)",
    humanCost: "Human Team",
    aiCost: "Surge Assist",
    currentCostLabel: "Current OpEx",
    newCostLabel: "Hybrid Cost (Human + AI)",
    surgeFeesLabel: "Automation Cost",
    resolvedTicketsLabel: "Automated Tickets",
    savingsLabel: "Estimated Monthly Savings",
    disclaimer: "Estimate based on opportunity cost. Real savings reflect team time freed up for higher-value activities.",
  },


  // Benchmarks
  benchmarks: {
    heroTitle: "Benchmarks & Research",
    heroSubtitle: "Verified market data to contextualize the cart abandonment problem.",
    
    disclaimerTitle: "Important",
    disclaimerText: "The benchmarks presented are market references, not guaranteed results. Real performance varies by niche, average ticket, base quality, and execution.",
    
    baymardTitle: "Cart Abandonment Rate",
    baymardStat: "70.22%",
    baymardDesc: "Average documented online shopping cart abandonment rate, according to compilation of 49 studies.",
    baymardSource: "Source: Baymard Institute",
    baymardLink: "https://baymard.com/lists/cart-abandonment-rate",
    
    klaviyoTitle: "Email Recovery",
    klaviyoStat: "3.33%",
    klaviyoStatTop: "7.69%",
    klaviyoDesc: "Average conversion rate (order placed) in abandoned cart flows. Top 10% achieve higher rate.",
    klaviyoContext: "Based on analysis of 143,000 abandoned cart flows in 2023.",
    klaviyoSource: "Source: Klaviyo",
    klaviyoLink: "https://www.klaviyo.com/blog/abandoned-cart-benchmarks",
    
    shopifyTitle: "Best Practices",
    shopifyDesc: "Educational reference on abandoned cart email examples and strategies.",
    shopifySource: "Source: Shopify Blog",
    shopifyLink: "https://www.shopify.com/blog/abandoned-cart-emails",
    
    contextTitle: "Important context",
    contextText: "These numbers serve as market reference. Real results depend on multiple factors including niche, average ticket, message timing, contact base quality, and content relevance.",
  },

  // About
  about: {
    heroTitle: "About ProcessSurge",
    heroSubtitle: "A tech company focused on automations and integrations with measurable results.",
    
    storyTitle: "Our story",
    storyText1: "ProcessSurge was born from frustration with automation solutions that promise a lot and deliver little. We believe technology should solve real problems, not create new ones.",
    storyText2: "We focus on e-commerce because that's where the impact of well-made automations is most measurable: recovered revenue, time saved, errors eliminated.",
    
    approachTitle: "Our approach",
    approach1Title: "Radical transparency",
    approach1Desc: "Clear definitions, auditable metrics, no impossible promises.",
    approach2Title: "Results focus",
    approach2Desc: "If it doesn't generate measurable results, it doesn't make sense to implement.",
    approach3Title: "Respect for end customer",
    approach3Desc: "Automation that protects the brand, not burns the contact base.",
    
    valuesTitle: "Values",
    value1: "Honesty about capabilities and limitations",
    value2: "Clear and accessible documentation",
    value3: "Responsive technical support",
    value4: "Continuous improvement based on data",
  },

  // Contact
  contact: {
    heroTitle: "Get in touch",
    heroSubtitle: "Tell us what you need and our team will reach out.",
    formTitle: "Talk to ProcessSurge",
    nameLabel: "Full name",
    namePlaceholder: "Your name",
    emailLabel: "Email",
    emailPlaceholder: "your@email.com",
    whatsappLabel: "WhatsApp",
    whatsappPlaceholder: "+1 555 123-4567",
    companyLabel: "Company",
    companyPlaceholder: "Company name",
    ecommerceUrlLabel: "Website, store, or profile link",
    ecommerceUrlPlaceholder: "e.g. https://yourstore.com",
    serviceLabel: "Main interest",
    servicePlaceholder: "Select",
    serviceStoreSetup: "Structure or launch the operation",
    serviceCartRecovery: "Abandoned cart recovery",
    serviceAiSupport: "AI and support",
    serviceIntegrations: "Integrations and automations",
    serviceData: "Data and dashboards",
    serviceNotSure: "Help me identify the best path",
    stageLabel: "Current stage",
    stagePlaceholder: "Select",
    stageStarting: "I am just getting started",
    stageSelling: "I already sell and want more organization",
    stageScaling: "I already have an operation and want to scale",
    stageDiagnosis: "I want to understand the best next step",
    roleLabel: "Role",
    rolePlaceholder: "Your role (optional)",
    platformLabel: "Platform",
    platformPlaceholder: "Select",
    platformVtex: "VTEX",
    platformShopify: "Shopify",
    platformNuvemshop: "Nuvemshop",
    platformYampi: "Yampi",
    platformWooCommerce: "WooCommerce",
    platformCartpanda: "Cartpanda",
    platformTray: "Tray",
    platformLoja_Integrada: "Loja Integrada",
    platformAppmax: "Appmax",
    platformBoth: "Both",
    platformNotSure: "Not sure",
    volumeLabel: "Monthly order volume",
    volumePlaceholder: "Select",
    volume1: "Up to 500 orders/month",
    volume2: "500 to 2,000 orders/month",
    volume3: "2,000 to 10,000 orders/month",
    volume4: "More than 10,000 orders/month",
    aovLabel: "Average order value",
    aovPlaceholder: "Select",
    aov1: "Up to $50",
    aov2: "$50 to $150",
    aov3: "$150 to $500",
    aov4: "More than $500",
    channelLabel: "Preferred contact channel",
    channelPlaceholder: "Select",
    channelWhatsApp: "WhatsApp",
    channelEmail: "Email",
    channelSms: "SMS",
    channelOther: "Other",
    couponLabel: "Do you use discount coupons?",
    couponYes: "Yes",
    couponNo: "No",
    couponNotSure: "Not sure",
    notesLabel: "Notes",
    notesPlaceholder: "Tell us more about your operation or what you need...",
    consentLabel: "I authorize contact for commercial purposes in accordance with privacy regulations.",
    submitButton: "Send message",
    submitting: "Sending...",
    successTitle: "Message sent!",
    successMessage: "We received your message and will reply soon.",
    errorTitle: "Error sending",
    errorMessage: "Something went wrong. Please try again or contact us on WhatsApp.",
    directContactTitle: "Direct contact",
    directContactEmail: "Email",
    directContactWhatsApp: "WhatsApp",
  },

  // Simulator
  simulator: {
    title: "Simulate potential impact",
    subtitle: "Estimate based on provided data. Actual results vary.",
    
    ordersLabel: "Orders per month",
    ordersPlaceholder: "e.g.: 1000",
    
    aovLabel: "Average order value ($)",
    aovPlaceholder: "e.g.: 50",
    
    abandonmentLabel: "Estimated abandonment rate (%)",
    abandonmentPlaceholder: "e.g.: 70",
    
    recoveryLabel: "Estimated recovery rate (%)",
    recoveryPlaceholder: "e.g.: 3",
    
    calculateButton: "Calculate",
    
    resultTitle: "Potential recoverable revenue",
    resultRange: "from {min} to {max}",
    resultMonthly: "per month",
    
    disclaimerTitle: "Important",
    disclaimerText: "This is a simplified estimate. Real results depend on niche, base quality, timing, message content, and many other factors. We do not guarantee results.",
    
    assumptionsTitle: "Calculation assumptions",
    assumption1: "Abandonment rate applied to reported orders",
    assumption2: "Recovery rate applied to abandoned carts",
    assumption3: "±30% variation in result to form the range",
  },

  // Blog
  blog: {
    heroTitle: "Blog",
    heroSubtitle: "Educational content about cart recovery, automation, and e-commerce.",
    
    nextTopicsTitle: "Coming topics",
    nextTopics: [
      "How to configure correct opt-in for cart recovery",
      "Differences between email, SMS, and WhatsApp recovery",
      "Metrics that matter: beyond open rate",
      "When automation hurts more than it helps",
      "Case study: anatomy of a recovery flow",
      "Privacy regulations and e-commerce: practical compliance guide",
    ],
    emptyState: "No posts yet. Check back soon.",
    postNotFound: "Post not found.",

  },

  // Legal Pages
  legal: {
  termsTitle: "Terms of Service",
  termsContent: `
## 1. Overview
Welcome to **ProcessSurge Tecnologia Ltda** ("ProcessSurge"), Tax ID (CNPJ) **64.342.437/0001-96**, located at R. Pais Leme, 215, Conj 1713, Pinheiros, São Paulo - SP, Brazil.

By accessing our automation, sales recovery, or intelligent support services, you agree to these terms.

## 2. The Service
ProcessSurge provides software and consulting for process automation, including:
- **Cart Recovery:** Active recovery of abandoned checkouts.
- **Surge Assist:** AI agents for triage and support.
- **Data Engineering:** Custom integrations and dashboards.

## 3. Acceptable Use (Anti-Spam Policy)
We have zero tolerance for Spam. By using our messaging tools (WhatsApp/Email), you agree to:
- Only contact leads with legitimate interest or opt-in.
- Immediately respect "Stop" requests.
- Never use purchased or cold lists (unsolicited Cold Outreach).

Violation of this clause will result in immediate account termination without refund.

## 4. Limitation of Liability
ProcessSurge provides tools "as is". We do not guarantee specific financial results, as these depend on the Client's product, pricing, and market. Our liability is limited to the amount paid for services in the last month.

## 5. Intellectual Property
Automation workflows, source codes, and dashboards developed by ProcessSurge remain the intellectual property of ProcessSurge, unless explicitly agreed upon in a "Work for Hire" contract. The Client receives a perpetual usage license for the duration of the contract.
  `,

  privacyTitle: "Privacy Policy",
  privacyContent: `
## 1. Introduction
This Privacy Policy describes how **ProcessSurge** collects, uses, and protects data, in compliance with the Brazilian General Data Protection Law (LGPD).

## 2. LGPD Roles
- **Client (You):** Acts as the **Controller** of your end consumers' data.
- **ProcessSurge (We):** Act as the **Processor**, processing data (name, phone, email, orders) strictly to execute the automations contracted by you.

## 3. Data Collection
We collect only data necessary for service execution:
- Checkout Data (Name, Phone, Email, Cart Items).
- Support History (Chat logs for AI training).
- Company Registration Data (for billing).

## 4. Security
We use encryption at rest and in transit. Our servers and databases are hosted in secure environments with restricted access via VPN and strong authentication.

## 5. Sharing
We do not sell data. We share information only with:
- **Infrastructure Providers:** (e.g., Contabo, AWS) for hosting.
- **Messaging APIs:** (e.g., Meta/WhatsApp) for message delivery.

## 6. Your Rights
As the Controller, you may request the deletion of data from our base at any time (except mandatory fiscal logs) by emailing **contato@processsurge.com**.
  `,

  commercialTitle: "Commercial Terms",
  commercialContent: `
## 1. Success Fee Model (Cart Recovery & Assist)
For success-based products, billing follows these rules:

### 1.1. Success Definition (Recovery)
A "Recovered Sale" is any order paid by the end customer within the attribution window (default: 24h to 72h) after our automation interaction.
- **Commission:** Percentage agreed in contract (default: 10% to 20%) on gross order value.

### 1.2. Success Definition (Assist)
A "Resolved Ticket" is when AI completes support without human handover, or qualifies a lead according to pre-set criteria.
- **Cost:** Fixed fee per resolution.

## 2. Third-Party Costs (WhatsApp/Meta)
ProcessSurge connects to the Client's own WhatsApp Business API (WABA) account.
- WhatsApp template/session costs are paid **directly by the Client to Meta (Facebook)**.
- ProcessSurge does not charge a markup on messages.

## 3. Billing and Payment
- **Closing:** Occurs on the last day of each month.
- **Due Date:** Invoice/Wire Transfer due on the 10th of the following month.
- **Delinquency:** Non-payment after 5 days of the due date results in temporary suspension of services and automations.

## 4. Custom Projects (Consulting)
For bespoke development (Custom Workflows), quotes are approved in advance and may involve:
- **Setup Fee:** One-time implementation fee (50% upfront, 50% on delivery).
- **Maintenance:** Monthly fee for monitoring and adjustments (optional).
  `
},

  // WhatsApp
  whatsapp: {
    message: "Hello! I came from the ProcessSurge website and would like to understand how you can help my operation.",
  },

  // Meta/SEO
  meta: {
    homeTitle: "ProcessSurge | Abandoned Cart Recovery",
    homeDesc: "Recover abandoned carts on VTEX and Shopify with performance model. No upfront costs, pay only for results.",
    productTitle: "ProcessSurge Cart Recovery | Product",
    productDesc: "Abandoned cart recovery solution for e-commerce with performance-based pricing.",
    howItWorksTitle: "How It Works | ProcessSurge",
    howItWorksDesc: "Understand the implementation process for ProcessSurge's abandoned cart recovery.",
    pricingTitle: "Pricing | ProcessSurge",
    pricingDesc: "Transparent and performance-based pricing model. Pay only for results.",
    servicesTitle: "Automation Services | ProcessSurge",
    servicesDesc: "Integrations, process automation, RPA, data extraction, and AI agents for e-commerce.",
    benchmarksTitle: "Benchmarks & Research | ProcessSurge",
    benchmarksDesc: "Verified market data on cart abandonment and recovery.",
    aboutTitle: "About | ProcessSurge",
    aboutDesc: "Learn about ProcessSurge, a tech company specialized in automations and integrations for e-commerce.",
    contactTitle: "Contact | ProcessSurge",
    contactDesc: "Get in touch with ProcessSurge to learn more about cart recovery.",
    blogTitle: "Blog | ProcessSurge",
    blogDesc: "Educational content about cart recovery, automation, and e-commerce.",
  },
};
